import { ChatInputSelectCampaign } from '@/components/AgentChat/ChatInputSelectCampaign';
import ImageLibraryModal from '@/modals/ImageLibraryModal/ImageLibraryModal';
import { PromptAttachment } from '@/types/agentChat';
import { AssetData, ImageAssetData } from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
} from '@superfunnel/ui-core/v2/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import Input from '@superfunnel/ui-core/v2/Input';
import Label from '@superfunnel/ui-core/v2/Label';
import { classNames } from '@superfunnel/utils';
import { MediaImage, MultiplePagesPlus, Plus } from 'iconoir-react';
import { ChangeEvent, Dispatch, SetStateAction, useRef, useState } from 'react';

interface IFileSelect {
  size?: 'sm' | 'lg';
  contextTypes?: ('file' | 'campaign' | 'asset')[];
  promptContexts: PromptAttachment[];
  setPromptContexts: Dispatch<SetStateAction<PromptAttachment[]>>;
  onFileUpload: (files: File[]) => void;
}
export const ChatInputContextSelectButton = ({
  size = 'sm',
  contextTypes,
  promptContexts,
  setPromptContexts,
  onFileUpload,
}: IFileSelect) => {
  const inputRef = useRef<any>(null);
  const [openModal, setOpenModal] = useState(false);
  const [openAssetModal, setOpenAssetModal] = useState(false);

  const handleFileSelection = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files?.length ? Array.from(e.target.files) : [];
    if (files.length > 0) {
      onFileUpload(files);
    }
    e.target.value = '';
  };

  const handleAssetSelected = (assets: AssetData[]) => {
    // Filter to only image assets and convert to PromptAttachment[]
    const assetAttachments: PromptAttachment[] = assets
      .filter((asset): asset is ImageAssetData => asset.type === 'image')
      .map((asset) => ({
        id: asset.id,
        type: 'asset' as const,
        asset,
      }));

    setPromptContexts((prev) => [...prev, ...assetAttachments]);
    setOpenAssetModal(false);
  };

  if (contextTypes && !contextTypes.length) {
    return null;
  }

  return (
    <div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            type="button"
            className={classNames(size === 'sm' ? 'size-8' : 'size-9')}
          >
            <Plus className="size-5.5" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align={'start'} className={'w-max p-2'}>
          {contextTypes?.includes('file') && (
            <DropdownMenuItem asChild>
              <label
                htmlFor={'agent-context-file-upload'}
                className={'hover:[&_svg]:text-base-900 p-2'}
              >
                <MultiplePagesPlus className={'size-5'} />
                Add photos & files
              </label>
            </DropdownMenuItem>
          )}
          {contextTypes?.includes('asset') && (
            <DropdownMenuItem
              onClick={() => setOpenAssetModal(true)}
              className={'hover:[&_svg]:text-base-900 p-2'}
            >
              <MediaImage className={'size-5'} />
              Select from library
            </DropdownMenuItem>
          )}
          {contextTypes?.includes('campaign') && (
            <ChatInputSelectCampaign
              promptContexts={promptContexts}
              setPromptContexts={setPromptContexts}
              asSubMenu={true}
            />
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <input
        id={'agent-context-file-upload'}
        name={'agent-context-file-upload'}
        type="file"
        ref={inputRef}
        accept="image/*, .pdf"
        multiple
        className="hidden"
        onChange={(e) => {
          handleFileSelection(e);
        }}
      />

      {/* Asset Library Modal */}
      <ImageLibraryModal
        multi={true}
        open={openAssetModal}
        types={['image']}
        onClose={() => setOpenAssetModal(false)}
        onSelected={handleAssetSelected}
      />

      <Dialog open={openModal} onOpenChange={setOpenModal}>
        <DialogContent className="sm:max-w-[425px]">
          <div className="grid gap-3">
            <Label htmlFor="name-1">Link</Label>
            <Input id="link" name="link" />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button type="submit">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
